import { AppNode, GroupNode } from "@features/builder/graph/types"
import { useEffect, useState } from "react"
import { useGraphDocument } from "@features/builder/GraphDocumentProvider/hooks/useGraphDocument"
import { NodeChange } from "@xyflow/react"
import { updateGraphDocumentGroups, updateGraphDocumentNodes } from "utils/crdt"

export const useLastNodesChanges = (nodes: AppNode[]) => {
  const graphDocument = useGraphDocument()
  const [lastNodesChanges, setLastNodesChanges] = useState<NodeChange<AppNode>[]>([])

  // sync nodes updates with yjs graph document
  useEffect(() => {
    if (!graphDocument) return

    // On first render react flow invokes  callback ("onNodesChange") to set correct sizes of
    // all visible nodes passed to the graph. It adds "measured" object with "width" and "height" to each node object.
    // We should just ignore these changes as they are not real changes for us.
    const isInitialChangeApplyingNodesSizes =
      lastNodesChanges.length === nodes.filter((n) => !n.hidden).length &&
      lastNodesChanges.every((change) => change.type === "dimensions" && change.dimensions)
    if (isInitialChangeApplyingNodesSizes) return

    const nodeIdToChangesMap = new Map<string, NodeChange<AppNode>[]>()
    for (const nodeChange of lastNodesChanges) {
      // `add` does not have an id, set temp id as `__newNode__`
      if (nodeChange.type === "add") {
        const existingChange = nodeIdToChangesMap.get("__newNode__") ?? []
        nodeIdToChangesMap.set("__newNode__", [...existingChange, nodeChange])
      } else {
        const existingChange = nodeIdToChangesMap.get(nodeChange.id) ?? []
        nodeIdToChangesMap.set(nodeChange.id, [...existingChange, nodeChange])
      }
    }

    for (const changes of nodeIdToChangesMap.values()) {
      // do not send info about resizing right and/or down
      if (changes.find((c) => c.type === "dimensions")) {
        return
      }

      // do not send info about resizing left and/or up
      if (
        changes.find((c) => c.type === "dimensions") &&
        changes.find((c) => c.type === "position")
      )
        return

      // do not send info about selection
      if (changes.find((c) => c.type === "select")) {
        return
      }

      // do not share moving node until dragging is done
      if (changes.find((c) => c.type === "position" && !!c.dragging)) {
        return
      }
    }

    graphDocument.transact(() => {
      const reactFlowNodes = nodes.filter((node) => node.id.startsWith("n")) as Exclude<
        AppNode,
        GroupNode
      >[]
      updateGraphDocumentNodes(graphDocument, reactFlowNodes)

      const reactFlowGroups = nodes.filter((node) => node.id.startsWith("g")) as GroupNode[]
      updateGraphDocumentGroups(graphDocument, reactFlowGroups)
    })
  }, [graphDocument, lastNodesChanges, nodes])

  return { lastNodesChanges, setLastNodesChanges }
}
